import React, { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  ExpansionPanel,
  ExpansionPanelActionEvent,
  ExpansionPanelContent,
  GridLayout,
  GridLayoutItem,
} from "@progress/kendo-react-layout";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import FormTextField from "../../components/formFields/FormTextField";
import {
  requiredValidator,
  skipValidator,
} from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import FormDatePicker from "../../components/formFields/FormDateField";
import moment from "moment";
import FormTextArea from "../../components/formFields/FormTextArea";
import { findAllActiveUnit } from "../unit/services/unit.services";
import { IUnit } from "../unit/unitModel";
import FormIncrementalSearch from "../../components/formFields/FormIncrementalSearch";
import { getAllItemIncremental } from "../Item/services/item.services";
import { IItem } from "../Item/itemModel";
import { getAllDistributorAndRetailers } from "../account/services/account.services";
import { getAllActiveEmployee } from "../employee/services/employee.services";
import { IEmployee } from "../employee/employeeModel";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { Button } from "@progress/kendo-react-buttons";
import { Reveal } from "@progress/kendo-react-animation";
import {
  createExportSalesOrder,
  createSingleExportSalesOrder,
  deleteExportSalesOrderItem,
  // createSingleExportSalesOrder,
  getExportSalesOrderByID,
  getExportSalesOrderByPONO,
  getExportSalesOrderListByID,
  getLatestPoNo,
  updateExportSalesOrder,
} from "./services/exporSalesOrder.services";
import IconButton from "../../components/common/IconButton";
import { FiEdit } from "react-icons/fi";
import { MdDelete } from "react-icons/md";
import {
  clearExportSalesOrderDetail,
  clearExportSalesOrderDetailByPONO,
  clearExportSalesOrderItemID,
  setExportSalesOrderDetail,
  setExportSalesOrderItemID,
} from "./exportSalesOrderSlice";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { Typography } from "@progress/kendo-react-common";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import { sumBy } from "lodash";
// import { setExportSalesOrderUpdateDetail } from "./exportSalesOrderSlice";
// import { DELETE_ACCESS, EDIT_ACCESS } from "../../_contstants/common";
// import { checkAcessRights } from "../../_helper/helper";

interface FormChangeWatcherProps {
  formRenderProps: FormRenderProps;
}

const ExportSalesOrderChangeWatcher: React.FC<FormChangeWatcherProps> = ({
  formRenderProps,
}) => {
  // TOTAL
  const dispatch = useAppDispatch();
  const location = useLocation();
  const ExportSalesOrderID = location.state?.ExportSalesOrderID;
  const isPONORef = useRef(true);
  const item_options = formRenderProps.valueGetter("item_options");
  const conversionUnitWeight =
    formRenderProps.valueGetter("conversion_unit_for_weight") || 0;
  const item_id = formRenderProps.valueGetter("item_id");
  const quantity_ctn = formRenderProps.valueGetter("quantity_ctn") || 0;
  const packing_size = formRenderProps.valueGetter("packing_size") || 0;
  const packing_weight = formRenderProps.valueGetter("packing_weight") || 0;
  const size_c = formRenderProps.valueGetter("size_c") || 0;
  const size_b = formRenderProps.valueGetter("size_b") || 0;
  const size_m = formRenderProps.valueGetter("size_m") || 0;
  const total = formRenderProps.valueGetter("total") || 0;

  useEffect(() => {
    if (item_id) {
      const conversion_unit_for_weight = item_options?.find(
        (item: any) => item.id === item_id
      )?.conversion_unit_for_weight;
      formRenderProps.onChange("total_kg", {
        value: (
          (conversion_unit_for_weight || conversionUnitWeight) *
          packing_weight *
          quantity_ctn
        ).toFixed(3),
      });

      formRenderProps.onChange("barcode", {
        value: `${quantity_ctn * packing_weight}`,
      });
    }
  }, [item_id, packing_size, packing_weight, quantity_ctn]);

  useEffect(() => {
    formRenderProps.onChange("total", {
      value: (size_c * size_b * size_m) / 1000000,
    });
  }, [size_c, size_b, size_m]);

  useEffect(() => {
    formRenderProps.onChange("sub_total", {
      value: total * quantity_ctn,
    });
  }, [total, quantity_ctn]);

  useEffect(() => {
    const fetchPoNo = async () => {
      const res = await dispatch(getLatestPoNo());
      if (res.meta.requestStatus === "fulfilled") {
        formRenderProps.onChange("pono", {
          value: res.payload?.Data?.pono || "",
        });
      }
    };

    if (isPONORef.current && !ExportSalesOrderID) {
      fetchPoNo();
      isPONORef.current = false;
    }
  }, [ExportSalesOrderID]);

  return null;
};

const CreateExportSalesOrder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const ExportSalesOrderID = location.state?.ExportSalesOrderID;
  const loading = useAppSelector((state) => state.exportsalesorder.loading);
  // const formLoading = useAppSelector(
  //   (state) => state.expensedetails.formLoading
  // );
  // const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  // const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);
  const UnitList = useAppSelector((state) => state.unit.UnitList);
  const AccountList = useAppSelector((state) => state.account.AccountList);
  const ExportSalesOrderDetail = useAppSelector(
    (state) => state.exportsalesorder.ExportSalesOrderDetail
  );

  const EmployeeList = useAppSelector((state) => state.employee.EmployeeList);

  const [expanded, setExpanded] = React.useState<any>(0);
  const [formKey, setFormKey] = React.useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [ExportSalesOrderDetail]);

  useEffect(() => {
    if (ExportSalesOrderID) {
      dispatch(getExportSalesOrderByID(ExportSalesOrderID));
      dispatch(getExportSalesOrderByPONO(`${ExportSalesOrderID}`));
    }
  }, [ExportSalesOrderID]);

  useEffect(() => {
    dispatch(findAllActiveUnit());
    dispatch(getAllActiveEmployee());
    dispatch(getAllDistributorAndRetailers());
    return () => {
      dispatch(clearExportSalesOrderDetailByPONO());
      dispatch(clearExportSalesOrderDetail());
    };
  }, []);

  const handleSubmit = async (values: any) => {
    if (ExportSalesOrderID) {
      if (values?.SUBMIT_TYPE === "single_item") {
        try {
          const singleinsertupdatepayload = {
            id: values?.ITEM_ID ? values?.ITEM_ID : 0,
            exportsalesorder_id: ExportSalesOrderID,
            item_id: values?.item_id ? values?.item_id : null,
            alias_name: values?.alias_name ? values?.alias_name : "",
            packing_size: values?.packing_size
              ? +values?.packing_size?.toFixed(3)
              : null,
            unit_id: values?.unit_id ? values?.unit_id : null,
            packing_unit_id: values?.packing_unit_id
              ? values?.packing_unit_id
              : null,
            packing_weight: values?.packing_weight
              ? values?.packing_weight
              : null,
            quantity_ctn: values?.quantity_ctn ? values?.quantity_ctn : null,
            total_kg: values?.total_kg ? +values?.total_kg : null,
            barcode: values?.barcode ? values?.barcode : "",
            user_id: values?.user_id ? values?.user_id : null,
            size_c: values?.size_c ? values?.size_c : null,
            size_b: values?.size_b ? values?.size_b : null,
            size_m: values?.size_m ? values?.size_m : null,
            total: values?.total ? values?.total : null,
            sub_total: values?.sub_total
              ? +values?.sub_total?.toFixed(3)
              : null,
          };
          const res = await dispatch(
            createSingleExportSalesOrder(singleinsertupdatepayload)
          );
          if (res.meta?.requestStatus === "fulfilled") {
            dispatch(
              setExportSalesOrderDetail({
                ...ExportSalesOrderDetail,
                export_sales_order_groupwise:
                  res.payload?.Data?.export_sales_order_groupwise,
              })
            );
          }
        } catch (error) {
          console.error("Error in handleSubmit:", error);
          throw error;
        }
      } else {
        try {
          const SalesOrderDetails = {
            export_sales_order_guid: values?.export_sales_order_guid
              ? values?.export_sales_order_guid
              : null,
            po_date: values?.po_date
              ? moment(values?.po_date).format("YYYY-MM-DD")
              : "",
            party_id: values?.party_id ? values?.party_id : null,
            exp_delivery_date: values?.exp_delivery_date
              ? moment(values?.exp_delivery_date).format("YYYY-MM-DD")
              : "",
            remarks: values?.remarks ? values?.remarks : "",
          };
          const response = await dispatch(
            updateExportSalesOrder(SalesOrderDetails)
          );
          if (response?.meta?.requestStatus === "fulfilled") {
            navigate("/exportsalesorder");
          }
        } catch (error) {
          console.error("Error in handleSubmit:", error);
          throw error;
        }
      }
    } else {
      try {
        const insertpayload = {
          // id: values?.id ? +values?.id : null,
          po_date: values?.po_date
            ? moment(values?.po_date).format("YYYY-MM-DD")
            : "",
          party_id: values?.party_id ? values?.party_id : null,
          exp_delivery_date: values?.exp_delivery_date
            ? moment(values?.exp_delivery_date).format("YYYY-MM-DD")
            : "",
          remarks: values?.remarks ? values?.remarks : "",
          item_id: values?.item_id ? values?.item_id : null,
          alias_name: values?.alias_name ? values?.alias_name : "",
          packing_size: values?.packing_size
            ? +values?.packing_size?.toFixed(3)
            : null,
          unit_id: values?.unit_id ? values?.unit_id : null,
          packing_unit_id: values?.packing_unit_id
            ? values?.packing_unit_id
            : null,
          packing_weight: values?.packing_weight
            ? values?.packing_weight
            : null,
          quantity_ctn: values?.quantity_ctn ? values?.quantity_ctn : null,
          total_kg: values?.total_kg ? +values?.total_kg : null,
          barcode: values?.barcode ? values?.barcode : "",
          user_id: values?.user_id ? values?.user_id : null,
          size_c: values?.size_c ? values?.size_c : null,
          size_b: values?.size_b ? values?.size_b : null,
          size_m: values?.size_m ? values?.size_m : null,
          total: values?.total ? values?.total : null,
          sub_total: values?.sub_total ? +values?.sub_total?.toFixed(3) : null,
        };
        const response = await dispatch(createExportSalesOrder(insertpayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate("/exportsalesorder/edit", {
            state: {
              ExportSalesOrderID: response?.payload.Data?.id,
            },
          });
        }
      } catch (error) {
        console.error("Error in handleSubmit:", error);
        throw error;
      }
    }
  };

  const handleFormClose = () => {
    navigate("/exportsalesorder");
  };
  const handleFormClear = (formRenderProps: FormRenderProps) => {
    ["ITEM_ID", "item_id", "unit_id", "packing_unit_id", "user_id"]?.map(
      (key: string) => {
        formRenderProps.onChange(key, { value: null });
      }
    );
    ["item_options"]?.map((key: string) => {
      formRenderProps.onChange(key, { value: [] });
    });
    [
      "packing_size",
      "packing_weight",
      "quantity_ctn",
      "total_kg",
      "size_c",
      "size_b",
      "size_m",
      "total",
      "sub_total",
      "alias_name",
      "barcode",
    ]?.map((key: string) => {
      formRenderProps.onChange(key, { value: "" });
    });
  };

  const handleItemSearchChange = useCallback(
    async (search: string, field: string, formRenderProps: FormRenderProps) => {
      const result = await dispatch(getAllItemIncremental({ search }));
      formRenderProps.onChange(field, {
        value: result.payload,
      });
    },
    [dispatch]
  );

  // const handleVendorSearchChange = useCallback(
  //   async (search: string, field: string, formRenderProps: FormRenderProps) => {
  //     const result = await dispatch(getAllDistributorAndRetailers(search));
  //     formRenderProps.onChange(field, {
  //       value: result.payload,
  //     });
  //   },
  //   [dispatch]
  // );
  const handleEdit = (
    id: number,
    self: any,
    formRenderProps: FormRenderProps
  ) => {
    const item = self.find((item: any) => item.id === id);
    [
      "item_id",
      "item_options",
      "alias_name",
      "packing_size",
      "unit_id",
      "packing_unit_id",
      "user_id",
      "packing_weight",
      "conversion_unit_for_weight",
      "quantity_ctn",
      "total_kg",
      "barcode",
      "size_c",
      "size_b",
      "size_m",
      "total",
      "sub_total",
    ].map((key: string) => {
      formRenderProps.onChange(key, { value: item[key] });
    });
    formRenderProps.onChange("ITEM_ID", { value: item["id"] });
    // dispatch(setExportSalesOrderUpdateDetail(item));
  };
  const handleOpenDeleteDialog = (id: number) => {
    dispatch(openDialog("deleteExportSalesOrderItem"));
    dispatch(setExportSalesOrderItemID(id));
  };
  const handleUnitChange = (e: any, formRenderProps: FormRenderProps) => {
    const item_options = formRenderProps.valueGetter("item_options");

    if (e?.value && item_options && item_options?.length > 0) {
      formRenderProps.onChange("unit_id", {
        value: item_options?.find((item: any) => item.id === e.value)?.unit_id,
      });
      formRenderProps.onChange("alias_name", {
        value: item_options?.find((item: any) => item.id === e.value)
          ?.product_name,
      });
    } else {
      formRenderProps.onChange("unit_id", {
        value: null,
      });
      formRenderProps.onChange("alias_name", {
        value: "",
      });
    }
  };

  // if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <DeleteExportSalesOrderItemDialog />
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={ExportSalesOrderDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12, marginBottom: 20 }}>
              <GridLayout
                style={{ marginRight: 20 }}
                gap={{ rows: 0, cols: 10 }}
                cols={[
                  { width: "33.33%" },
                  { width: "33.33%" },
                  { width: "33.33%" },
                ]}
              >
                <GridLayoutItem colSpan={3}>
                  <p className="fs-4" style={{ margin: 0, fontWeight: "bold" }}>
                    {ExportSalesOrderID
                      ? "Update Export Sales Order"
                      : "Add Export Sales Order"}
                  </p>
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="pono"
                    label="Po. No."
                    placeholder="Po. No"
                    // format="dd/MM/yyyy"
                    component={FormTextField}
                    disabled
                    // validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="po_date"
                    label="Po. Date"
                    format="dd/MM/yyyy"
                    component={FormDatePicker}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="party_id"
                    label="Party Name"
                    placeholder="Select Party Name"
                    component={FormSelectionField}
                    options={AccountList.map((account: any) => ({
                      value: account.id,
                      label: account.account_name,
                    }))}
                    validator={requiredValidator}
                    astrike={true}
                  />
                  {/* <Field
                    name="party_id"
                    label="Party Name"
                    placeholder="Search Party Name"
                    // isAddNew={true}
                    // addNewLink="account/create"
                    component={FormIncrementalSearch}
                    validator={requiredValidator}
                    astrike={true}
                    fetchIncrementalData={(search: any) =>
                      handleVendorSearchChange(
                        search,
                        `vendor_options`,
                        formRenderProps
                      )
                    }
                    options={
                      formRenderProps
                        .valueGetter("vendor_options")
                        ?.map((item: any) => {
                          return {
                            value: item.id,
                            label: item.account_name,
                          };
                        }) || []
                    }
                  /> */}
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="exp_delivery_date"
                    label="Exp. Delivery Date"
                    format="dd/MM/yyyy"
                    component={FormDatePicker}
                    validator={requiredValidator}
                    minDate={
                      moment(formRenderProps.valueGetter("po_date")).toDate() ||
                      new Date(1900, 0, 1)
                    }
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field
                    name="remarks"
                    label="Remarks"
                    placeholder="Remarks.."
                    component={FormTextArea}
                    rows={2}
                  />
                </GridLayoutItem>
                {ExportSalesOrderID && (
                  <>
                    <GridLayoutItem
                      colSpan={3}
                      style={{
                        textAlign: "end",
                        marginTop: "20px",
                        width: "100%",
                      }}
                    >
                      <ButtonWithLoading
                        label="Update"
                        type="submit"
                        onClick={() =>
                          formRenderProps.onChange("SUBMIT_TYPE", {
                            value: "main_sales_order",
                          })
                        }
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 4 }}
                        onClick={() => navigate("/exportsalesorder")}
                      >
                        Cancel
                      </Button>
                    </GridLayoutItem>
                  </>
                )}
              </GridLayout>
            </ShadowCard>
            <ShadowCard style={{ padding: 12, marginBottom: 20 }}>
              <GridLayout
                style={{ marginRight: 40 }}
                gap={{ rows: 0, cols: 10 }}
                cols={[
                  { width: "25%" },
                  { width: "25%" },
                  { width: "25%" },
                  { width: "25%" },
                ]}
              >
                <GridLayoutItem colSpan={4}>
                  <p className="fs-4" style={{ margin: 0, fontWeight: "bold" }}>
                    {formRenderProps.valueGetter("ITEM_ID")
                      ? "Update Item"
                      : "Add Item"}
                  </p>
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="item_id"
                    label="Item Name"
                    placeholder="Search Item"
                    // isAddNew={true}
                    // addNewLink="item/create"
                    component={FormIncrementalSearch}
                    fetchIncrementalData={(search: any) =>
                      handleItemSearchChange(
                        search,
                        `item_options`,
                        formRenderProps
                      )
                    }
                    validator={
                      !ExportSalesOrderID ? requiredValidator : skipValidator
                    }
                    astrike={!ExportSalesOrderID ? true : false}
                    onChange={(e: any) => handleUnitChange(e, formRenderProps)}
                    options={
                      formRenderProps
                        .valueGetter("item_options")
                        ?.map((item: IItem) => {
                          return {
                            value: item.id,
                            label: item.product_name,
                          };
                        }) || []
                    }
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="alias_name"
                    label="Alias Name"
                    component={FormTextField}
                    placeholder="Alias Name"
                    // validator={requiredValidator}
                    // astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="user_id"
                    label="Person"
                    placeholder="Select Person"
                    component={FormSelectionField}
                    options={EmployeeList.map((emp: IEmployee) => ({
                      value: emp.id,
                      label: emp.name,
                    }))}
                    // validator={requiredValidator}
                    // astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  style={{ display: "flex", gap: "10px" }}
                  colSpan={4}
                >
                  <Field
                    wrapperClassName="w-100"
                    name="packing_size"
                    label="Size"
                    type="number"
                    placeholder="0"
                    component={FormNumericTextField}
                    validator={
                      !ExportSalesOrderID ? requiredValidator : skipValidator
                    }
                    astrike={!ExportSalesOrderID ? true : false}
                  />
                  <Field
                    wrapperClassName="w-100"
                    name="unit_id"
                    label="Unit"
                    placeholder="Select Unit"
                    component={FormSelectionField}
                    validator={
                      !ExportSalesOrderID ? requiredValidator : skipValidator
                    }
                    astrike={!ExportSalesOrderID ? true : false}
                    options={UnitList?.map((unit: IUnit) => {
                      return {
                        value: unit?.id,
                        label: unit?.unit_code,
                      };
                    })}
                  />
                  <Field
                    wrapperClassName="w-100"
                    name="packing_weight"
                    label="Weight"
                    placeholder="0"
                    component={FormNumericTextField}
                    validator={
                      !ExportSalesOrderID ? requiredValidator : skipValidator
                    }
                    astrike={!ExportSalesOrderID ? true : false}
                  />
                  <Field
                    wrapperClassName="w-100"
                    name="packing_unit_id"
                    label="Packing Unit"
                    placeholder="Select Packing Unit"
                    component={FormSelectionField}
                    validator={
                      !ExportSalesOrderID ? requiredValidator : skipValidator
                    }
                    astrike={!ExportSalesOrderID ? true : false}
                    options={UnitList?.map((unit: IUnit) => {
                      return {
                        value: unit?.id,
                        label: unit?.unit_code,
                      };
                    })}
                  />
                  <Field
                    wrapperClassName="w-100"
                    name="quantity_ctn"
                    label="Quantity (CTNS)"
                    placeholder="0"
                    component={FormNumericTextField}
                    validator={
                      !ExportSalesOrderID ? requiredValidator : skipValidator
                    }
                    astrike={!ExportSalesOrderID ? true : false}
                  />
                  {/* </GridLayoutItem>
                <GridLayoutItem> */}
                  <Field
                    wrapperClassName="w-100"
                    name="total_kg"
                    label="Total (KG)"
                    placeholder="0"
                    component={FormTextField}
                    disabled={true}
                    // validator={requiredValidator}
                    // astrike={true}
                  />
                  {/* </GridLayoutItem>
                <GridLayoutItem> */}
                  <Field
                    wrapperClassName="w-100"
                    name="barcode"
                    disabled={true}
                    label="BARCODE"
                    placeholder="0"
                    component={FormTextField}
                    // validator={requiredValidator}
                    // astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem
                  style={{ display: "flex", gap: "10px" }}
                  colSpan={2}
                >
                  <Field
                    name="size_c"
                    label="C"
                    placeholder="0"
                    type="number"
                    wrapperStyle={{ width: "33.33%" }}
                    component={FormTextField}
                    // validator={requiredValidator}
                    // astrike={true}
                  />
                  <Field
                    name="size_b"
                    label="B"
                    placeholder="0"
                    type="number"
                    wrapperStyle={{ width: "33.33%" }}
                    component={FormTextField}
                    // validator={requiredValidator}
                    // astrike={true}
                  />
                  <Field
                    name="size_m"
                    label="M"
                    placeholder="0"
                    type="number"
                    wrapperStyle={{ width: "33.33%" }}
                    component={FormTextField}
                    // validator={requiredValidator}
                    // astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="total"
                    placeholder="0"
                    label="Total"
                    component={FormTextField}
                    disabled={true}
                    // validator={requiredValidator}
                    // astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="sub_total"
                    placeholder="0"
                    label="Sub Total"
                    format="n3"
                    component={FormNumericTextField}
                    disabled={true}
                    // validator={requiredValidator}
                    // astrike={true}
                  />
                </GridLayoutItem>
                {ExportSalesOrderID && (
                  <GridLayoutItem
                    colSpan={4}
                    style={{
                      textAlign: "end",
                      marginTop: "20px",
                      width: "100%",
                    }}
                  >
                    <ButtonWithLoading
                      label={
                        formRenderProps.valueGetter("ITEM_ID")
                          ? "Update"
                          : "Add"
                      }
                      type="submit"
                      onClick={() =>
                        formRenderProps.onChange("SUBMIT_TYPE", {
                          value: "single_item",
                        })
                      }
                      disabled={
                        !formRenderProps.allowSubmit ||
                        loading ||
                        !formRenderProps.valueGetter("item_id") ||
                        !formRenderProps.valueGetter("packing_size") ||
                        !formRenderProps.valueGetter("unit_id") ||
                        !formRenderProps.valueGetter("packing_weight") ||
                        !formRenderProps.valueGetter("packing_unit_id") ||
                        !formRenderProps.valueGetter("quantity_ctn")
                      }
                      loading={loading}
                    />
                    {/* {formRenderProps.valueGetter("ITEM_ID") && ( */}
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 4 }}
                      onClick={() => handleFormClear(formRenderProps)}
                    >
                      Clear
                    </Button>
                    {/* )} */}
                  </GridLayoutItem>
                )}
                {!ExportSalesOrderID && (
                  <GridLayoutItem
                    colSpan={4}
                    style={{
                      textAlign: "end",
                      marginTop: "20px",
                      width: "100%",
                    }}
                  >
                    <ButtonWithLoading
                      label="Save"
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 4 }}
                      onClick={handleFormClose}
                    >
                      Cancel
                    </Button>
                  </GridLayoutItem>
                )}
              </GridLayout>
            </ShadowCard>
            {ExportSalesOrderID && (
              <ShadowCard style={{ padding: 12 }}>
                <table
                  cellSpacing={1}
                  cellPadding={2}
                  className="table table-bordered w-100  salesorder-print-table"
                  style={{ marginTop: 1 }}
                >
                  <tr
                    className="text-center fw-600 text-white bg-dark"
                    style={{ fontSize: 12 }}
                  >
                    <th className="pb-3 pt-3" style={{ width: "3%" }}>
                      No.
                    </th>
                    <th style={{ width: "10%" }}>Item Name</th>
                    <th style={{ width: "8%" }}>Alias Name</th>
                    <th style={{ width: "17%" }}>Packing</th>
                    <th style={{ width: "5%" }}>Cartoon /Bag /Pkt</th>
                    <th style={{ width: "8%" }}>Total KG</th>
                    <th style={{ width: "8%" }}>Barcode</th>
                    <th style={{ width: "8%" }}>P.Name</th>
                    <th style={{ width: "3%" }}>C</th>
                    <th style={{ width: "3%" }}>B</th>
                    <th style={{ width: "3%" }}>M</th>
                    <th style={{ width: "9%" }}>Total</th>
                    <th style={{ width: "9%" }}>Sub Total</th>
                    <th style={{ width: "5%" }}>Actions</th>
                  </tr>
                  {ExportSalesOrderDetail?.export_sales_order_groupwise &&
                  ExportSalesOrderDetail?.export_sales_order_groupwise.length >
                    0 ? (
                    ExportSalesOrderDetail?.export_sales_order_groupwise?.map(
                      (item: any, mainIndex: number) => (
                        <>
                          <tr key={mainIndex}>
                            <td colSpan={15} className="p-0">
                              <ExpansionPanel
                                className="p-0"
                                title={item?.item_group}
                                expanded={expanded === mainIndex}
                                tabIndex={0}
                                key={item.item_group_id}
                                onAction={(
                                  event: ExpansionPanelActionEvent
                                ) => {
                                  setExpanded(
                                    event.expanded ? null : mainIndex
                                  );
                                }}
                              >
                                <Reveal className="p-0" style={{ zIndex: "1" }}>
                                  {expanded === mainIndex && (
                                    <ExpansionPanelContent className="sales-order-hover-class export-sales-order-expand p-0">
                                      <div
                                        id={`collapse${mainIndex}`}
                                        className="p-0"
                                      >
                                        <div className="p-0">
                                          <table
                                            className="export-sales-order-expand-table"
                                            style={{
                                              width: "100%",
                                              color: "#000",
                                            }}
                                          >
                                            {item?.export_sales_Order_itemwise &&
                                              item?.export_sales_Order_itemwise
                                                ?.length > 0 &&
                                              item?.export_sales_Order_itemwise?.map(
                                                (
                                                  subitem: any,
                                                  subIndex: number,
                                                  self: any
                                                ) => (
                                                  <tr key={subIndex}>
                                                    <td
                                                      style={{
                                                        width: "3%",
                                                      }}
                                                      className="text-center"
                                                    >
                                                      {subIndex + 1}
                                                    </td>
                                                    <td
                                                      style={{
                                                        width: "10%",
                                                      }}
                                                    >
                                                      {subitem?.item_name}
                                                    </td>
                                                    <td
                                                      style={{
                                                        width: "8%",
                                                      }}
                                                    >
                                                      {subitem?.alias_name}
                                                    </td>
                                                    <td
                                                      style={{
                                                        width: "5%",
                                                      }}
                                                      className="text-center"
                                                    >
                                                      {subitem?.packing_size}
                                                    </td>
                                                    <td
                                                      style={{
                                                        width: "5%",
                                                      }}
                                                      className="text-center"
                                                    >
                                                      {subitem?.unit_code}
                                                    </td>
                                                    <td
                                                      style={{
                                                        width: "2%",
                                                      }}
                                                      className="text-center"
                                                    >
                                                      X
                                                    </td>
                                                    <td
                                                      style={{
                                                        width: "5%",
                                                      }}
                                                      className="text-center"
                                                    >
                                                      {subitem?.packing_weight}{" "}
                                                      {subitem?.packing_unit}
                                                    </td>
                                                    <td
                                                      style={{
                                                        width: "5%",
                                                      }}
                                                      className="text-center"
                                                    >
                                                      {subitem?.quantity_ctn}
                                                    </td>
                                                    <td
                                                      style={{
                                                        width: "8%",
                                                      }}
                                                      className="text-center"
                                                    >
                                                      {subitem?.total_kg}
                                                    </td>
                                                    <td
                                                      style={{
                                                        width: "8%",
                                                      }}
                                                      className="text-center"
                                                    >
                                                      {subitem?.barcode}
                                                    </td>
                                                    <td
                                                      style={{
                                                        width: "8%",
                                                      }}
                                                    >
                                                      {subitem?.user_name}
                                                    </td>
                                                    <td
                                                      style={{
                                                        width: "3%",
                                                      }}
                                                      className="text-center"
                                                    >
                                                      {subitem?.size_c}
                                                    </td>
                                                    <td
                                                      style={{
                                                        width: "3%",
                                                      }}
                                                      className="text-center"
                                                    >
                                                      {subitem?.size_b}
                                                    </td>
                                                    <td
                                                      style={{
                                                        width: "3%",
                                                      }}
                                                      className="text-center"
                                                    >
                                                      {subitem?.size_m}
                                                    </td>
                                                    <td
                                                      style={{
                                                        width: "9%",
                                                      }}
                                                      className="text-end"
                                                    >
                                                      {subitem?.total}
                                                    </td>
                                                    <td
                                                      style={{
                                                        width: "9%",
                                                      }}
                                                      className="text-end"
                                                    >
                                                      {subitem?.sub_total?.toFixed(
                                                        3
                                                      )}
                                                    </td>
                                                    <td
                                                      style={{
                                                        width: "5%",
                                                      }}
                                                      className="text-end"
                                                    >
                                                      <div
                                                        style={{
                                                          display: "flex",
                                                          justifyContent:
                                                            "center",
                                                        }}
                                                      >
                                                        {/* {isEditAccess && ( */}
                                                        <IconButton
                                                          type="button"
                                                          fillMode="flat"
                                                          size="small"
                                                          title="Edit"
                                                          onClick={() =>
                                                            handleEdit(
                                                              subitem?.id,
                                                              self,
                                                              formRenderProps
                                                            )
                                                          }
                                                        >
                                                          <FiEdit
                                                            className="absolute-position"
                                                            style={{
                                                              fontSize: "20px",
                                                            }}
                                                          />
                                                        </IconButton>
                                                        {/* )} */}
                                                        {/* {isDeleteAccess && ( */}
                                                        <IconButton
                                                          type="button"
                                                          fillMode="flat"
                                                          size="small"
                                                          title="Delete"
                                                          onClick={() =>
                                                            handleOpenDeleteDialog(
                                                              subitem?.id
                                                            )
                                                          }
                                                        >
                                                          <MdDelete
                                                            className="absolute-position"
                                                            style={{
                                                              fontSize: "24px",
                                                            }}
                                                          />
                                                        </IconButton>
                                                        {/* )} */}
                                                      </div>
                                                    </td>
                                                  </tr>
                                                )
                                              )}
                                          </table>
                                        </div>
                                      </div>
                                    </ExpansionPanelContent>
                                  )}
                                </Reveal>
                              </ExpansionPanel>
                            </td>
                          </tr>
                        </>
                      )
                    )
                  ) : (
                    <tr>
                      <td colSpan={15} className="text-center p-2">
                        No Records Found
                      </td>
                    </tr>
                  )}
                  {ExportSalesOrderDetail?.export_sales_order_groupwise &&
                    ExportSalesOrderDetail?.export_sales_order_groupwise
                      .length > 0 && (
                      <tr>
                        <td
                          colSpan={4}
                          style={{
                            border: "1px solid",
                            textAlign: "end",
                            fontWeight: "bold",
                            padding: 5,
                          }}
                        >
                          Total Quantity
                        </td>
                        <td
                          style={{
                            textAlign: "center",
                            border: "1px solid",
                            fontWeight: "bold",
                          }}
                        >
                          {sumBy(
                            ExportSalesOrderDetail.export_sales_order_groupwise.flatMap(
                              (group) => group.export_sales_Order_itemwise
                            ),
                            "quantity_ctn"
                          )}
                        </td>
                        <td
                          style={{
                            textAlign: "center",
                            border: "1px solid",
                            fontWeight: "bold",
                          }}
                        >
                          {sumBy(
                            ExportSalesOrderDetail.export_sales_order_groupwise.flatMap(
                              (group) => group.export_sales_Order_itemwise
                            ),
                            "total_kg"
                          )?.toFixed(3)}
                        </td>
                        <td
                          colSpan={6}
                          style={{
                            border: "1px solid",
                            textAlign: "end",
                            fontWeight: "bold",
                            padding: 5,
                          }}
                        >
                          Total Cubic Meter
                        </td>
                        <td
                          style={{
                            textAlign: "center",
                            border: "1px solid",
                            fontWeight: "bold",
                          }}
                        >
                          {sumBy(
                            ExportSalesOrderDetail.export_sales_order_groupwise.flatMap(
                              (group) => group.export_sales_Order_itemwise
                            ),
                            "sub_total"
                          )?.toFixed(3)}
                        </td>
                        <td style={{ border: "1px solid" }}></td>
                      </tr>
                    )}
                </table>
                {!ExportSalesOrderID && (
                  <GridLayoutItem
                    style={{
                      textAlign: "end",
                      marginTop: "20px",
                      width: "100%",
                    }}
                  >
                    <ButtonWithLoading
                      label="Save"
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 4 }}
                      onClick={handleFormClose}
                    >
                      Cancel
                    </Button>
                  </GridLayoutItem>
                )}
              </ShadowCard>
            )}
            <ExportSalesOrderChangeWatcher formRenderProps={formRenderProps} />
          </FormElement>
        )}
      />
    </>
  );
};

const DeleteExportSalesOrderItemDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const ExportSalesOrderID = location.state?.ExportSalesOrderID;
  const ExportSalesOrderItemID = useAppSelector(
    (state) => state.exportsalesorder.ExportSalesOrderItemID
  );
  const dialogName = useAppSelector((state) => state.dialog.dialogName);

  const handleDeleteAction = async (ID: any) => {
    if (ID) {
      const response = await dispatch(deleteExportSalesOrderItem(ID));
      if (response.payload.Data.Status === 200) {
        dispatch(getExportSalesOrderListByID(ExportSalesOrderID));
        dispatch(closeDialog());
        dispatch(clearExportSalesOrderItemID());
      } else {
        dispatch(getExportSalesOrderListByID(ExportSalesOrderID));
        dispatch(closeDialog());
        dispatch(clearExportSalesOrderItemID());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteExportSalesOrderItem" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Export Sales Order Item?"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Export Sales Order Item?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(ExportSalesOrderItemID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default CreateExportSalesOrder;
